import { TableColumnFormat } from 'common/visualizations/views/agGridReact/types';
import DragDropContainer, { DragDropContainerType } from 'common/components/DragDropContainer';
import { ViewColumn } from 'common/types/viewColumn';
import React, { useEffect, useState } from 'react';
import { I18n } from 'common/i18n';

import './unique-values.scss';
import { isEmpty, isEqual } from 'lodash';
import { modalValuesDataFormatter } from 'common/visualizations/views/agGridReact/helpers/TableColumnFormatting';

// Type for column value that can be a URL object or primitive
export type ColumnValue = string | number | boolean | null | UrlValue;

// Type for URL values in cells
export interface UrlValue {
  url?: string;
  [key: string]: string | undefined;
}

// Type for row object from the API (similar to NormalRow in PDF preview)
export interface UniqueValueRow extends Record<string, ColumnValue> {}

// Type representing "no value" - an empty object
export type EmptyObject = Record<string, never>;

// Type for processed unique value with formatted display
export interface ProcessedUniqueValue {
  key: UniqueValueRow | EmptyObject; // Original data or empty object for "no value" case
  value: string; // Formatted HTML string for display
}

interface UniqueValuesContainerProps {
  columnsToCustomSort: ViewColumn[];
  columnFormats: { [key: string]: TableColumnFormat };
  data: UniqueValueRow[];
  isCustomSortModalOpen: boolean;
  columnToSort: string | null;
  setIsApplyDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  initialUniqueValues: UniqueValueRow[];
  setNewUniqueValues: React.Dispatch<React.SetStateAction<ColumnValue[]>>;
  setIsColumnSelectionDisabled: (isColumnSelectionDisabled: boolean) => void;
}

const UniqueValuesContainer: React.FC<UniqueValuesContainerProps> = ({
  columnsToCustomSort,
  columnFormats,
  data,
  isCustomSortModalOpen,
  setIsApplyDisabled,
  initialUniqueValues,
  setNewUniqueValues,
  columnToSort,
  setIsColumnSelectionDisabled,
}) => {
  const [uniqueValues, setUniqueValues] = useState<ProcessedUniqueValue[]>([]);
  const [initialProcessedUniqueValues, setInitialProcessedUniqueValues] = useState<ProcessedUniqueValue[]>(
    [],
  );

  const isUrl = (value: ColumnValue): value is UrlValue => {
    return typeof value === 'object' && value !== null;
  };

  const isNoValue = (datum: UniqueValueRow): datum is EmptyObject => {
    return isEqual(datum, {}); // Checks if the object has no properties
  };

  const processData = (unprocessedData: UniqueValueRow[]): ProcessedUniqueValue[] => {
    return unprocessedData.map((datum) => {
      const value = !isNoValue(datum) ? Object.values(datum)[0] : null;
      let processedDatum: string = '';

      if (value === null || value === undefined) {
        processedDatum = '';
      } else if (isUrl(value)) {
        const objectValues = Object.values(value);
        processedDatum = String(objectValues[objectValues.length - 1] || '');
      } else {
        processedDatum =
          modalValuesDataFormatter({
            viewColumns: columnsToCustomSort,
            value: value,
            selectedColumn: columnToSort,
            columnFormats: columnFormats,
          }) || '';
      }

      return {
        key: datum,
        value: processedDatum,
      };
    });
  };

  useEffect(() => {
    const processedInitialData = processData(initialUniqueValues);
    setInitialProcessedUniqueValues(processedInitialData);
  }, [initialUniqueValues, columnFormats]);

  useEffect(() => {
    const processedData = processData(data);
    setUniqueValues(processedData);
  }, [data, columnFormats]);

  const valuesHtml = uniqueValues.map((uniqueValue) => {
    let html = uniqueValue.value;
    if (isEmpty(html)) {
      html = I18n.t('shared.visualizations.charts.common.summary_table.no_value');
    }

    let key: React.Key;
    if (isNoValue(uniqueValue.key)) {
      key = html;
    } else {
      const columnValue = uniqueValue.key[columnToSort as string];
      key = columnValue !== null && columnValue !== undefined ? String(columnValue) : html;
    }
    return (
      <span
        key={key}
        dangerouslySetInnerHTML={{
          __html: html,
        }}
      />
    );
  });

  const wrappedValuesHtml = valuesHtml.map((item, i) => ({
    dragAndDroppable: true,
    element: item,
    additionalItemProps: {
      'data-testid': `custom-sort-order-unique-value-${i}`,
    },
  }));

  const handleDrop = (newItems: ProcessedUniqueValue[]) => {
    setIsApplyDisabled(isEqual(newItems, initialProcessedUniqueValues));
    setIsColumnSelectionDisabled(!isEqual(newItems, initialProcessedUniqueValues));
    setUniqueValues(newItems);
    setNewUniqueValues(
      newItems.map((newItem) => {
        if (isEmpty(newItem.key)) {
          return {};
        } else {
          const datum = newItem.key[columnToSort as string];
          return datum !== undefined ? datum : null;
        }
      }),
    );
  };

  return (
    <div>
      {isCustomSortModalOpen && (
        <DragDropContainer
          type={DragDropContainerType.FORGE_LIST}
          items={uniqueValues}
          onDrop={handleDrop}
          iconName="drag_handle"
          childElements={wrappedValuesHtml}
          className={'unique-values'}
          sourceComponent={'custom-sort'}
        />
      )}
    </div>
  );
};

export default UniqueValuesContainer;
